<script setup lang="ts">
import Button from '~/components/design-system/button.vue';
import FacebookIcon from '~/components/design-system/icons/FacebookIcon.vue';
import TwitterIcon from '~/components/design-system/icons/TwitterIcon.vue';
import WhatsappIcon from '~/components/design-system/icons/WhatsappIcon.vue';
import { EnvelopeIcon } from '@heroicons/vue/24/outline';
import { ShareIcon } from '@heroicons/vue/24/outline';

interface IShareProps {
  type?: 'file' | 'social';
  shareText?: string;
  shareTitle?: string;
  shareUrl?: string | null;
  el?: HTMLElement;
  useDefaultUrl?: boolean;
  inModal?: boolean;
  disabled?: boolean;
}

const emits = defineEmits<{
  (
    e: 'share',
    platform: 'whatsapp' | 'facebook' | 'twitter' | 'email' | 'file-share',
  ): void;
}>();

const props = withDefaults(defineProps<IShareProps>(), {
  type: 'social',
  shareText: 'game-share-text',
  shareTitle: 'game-share-title',
  shareUrl: null,
  el: undefined,
  inModal: false,
  disabled: false,
});

const { $t } = useNuxtApp();

const {
  shareElementAsImageFile,
  canShareFiles,
  whatsappShare,
  facebookShare,
  twitterShare,
  emailShare,
} = useShare();

const socials = computed(() => {
  return [
    {
      id: 'whatsapp',
      icon: WhatsappIcon,
      action: () => {
        whatsappShare(props.shareText, props.shareUrl);
        emits('share', 'whatsapp');
      },
    },
    {
      id: 'facebook',
      icon: FacebookIcon,
      action: () => {
        facebookShare(props.shareUrl);
        emits('share', 'facebook');
      },
    },
    {
      id: 'twitter',
      icon: TwitterIcon,
      action: () => {
        twitterShare(props.shareText, props.shareUrl);
        emits('share', 'twitter');
      },
    },
    {
      id: 'email',
      icon: EnvelopeIcon,
      action: () => {
        emailShare(props.shareText, props.shareUrl);
        emits('share', 'email');
      },
    },
  ];
});
</script>

<template>
  <div :class="{ 'space-y-4': !!$slots?.title }">
    <slot name="title" />

    <div
      v-if="type === 'social' || !canShareFiles"
      class="grid grid-cols-4 gap-1"
    >
      <Button
        v-for="(social, index) in socials"
        :key="index"
        class="h-10 mx-auto"
        :type="inModal ? 'secondary' : 'secondary-alternative'"
        shape="square"
        padding="sm"
        :disabled="disabled"
        :aria-disabled="disabled"
        @click="social.action()"
      >
        <component
          :is="social.icon"
          :key="social.id"
          class="text-base-priority mx-auto"
        />
      </Button>
    </div>

    <LazyDesignSystemButton
      v-else-if="type === 'file' && canShareFiles"
      type="secondary"
      padding="wide-short"
      rounding="md"
      :disabled="disabled"
      :aria-disabled="disabled"
      class="gap-2 justify-center items-center"
      @click="[
        shareElementAsImageFile(
          props.el,
          {
            fileName: 'My Big Win',
            format: 'png',
            backgroundColor: 'transparent',
          },
          {
            title: props.shareTitle,
            text: props.shareText,
          },
        ),
        emits('share', 'file-share'),
      ]"
    >
      <ShareIcon />
      <span>{{ $t('share') }}</span>
    </LazyDesignSystemButton>
  </div>
</template>
